// ── Step times dialog — when did each step start and finish? ─────────────────
//
// Mounted once in Layout. Opened two ways (utils/stepTimes.js): by the axios
// interceptor when the server refuses a lot moving on without its step times —
// then only the missing steps are asked, and the original request is retried on
// save — or by a "Fill in times" button, which shows every step of the lot.
//
// Picking a time should take one tap: Now, 15 / 30 / 60 minutes ago, or a time the
// records already hold ("Arrival submitted 11:42"). A typed time is the fallback.
// Each step is Exact, Approximate (needs a reason, shown with "~") or Not known
// (needs a reason) — so the dialog can always be answered and never stops the plant.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { registerStepTimesHandler, announceStepTimesChanged } from '../../utils/stepTimes';

const CERTAINTY = [['exact', 'Exact'], ['approximate', 'Approximate'], ['unknown', 'Not known']];
const AGO = [[0, 'Now'], [15, '15 min ago'], [30, '30 min ago'], [60, '1 h ago']];

export const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return d.toLocaleString(undefined, sameDay
    ? { hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const AFTER = [[15, '+15 min'], [30, '+30 min'], [60, '+1 h'], [120, '+2 h'], [180, '+3 h']];

// `recent`: offer Now / minutes-ago only for work happening now — on a lot from last
// week they are noise. `after`: once a start is picked, the finish is one tap away.
function TimeField({ label, value, onChange, suggestions, recent, after }) {
  const pick = (iso) => onChange(iso);
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <span className={`text-sm font-semibold tabular-nums ${value ? 'text-slate-800' : 'text-slate-400'}`}>
          {value ? fmtTime(value) : 'not set'}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {after && AFTER.map(([mins, text]) => {
          const iso = new Date(new Date(after).getTime() + mins * 60000).toISOString();
          if (new Date(iso) > new Date(Date.now() + 5 * 60000)) return null;
          return (
            <button key={`a${mins}`} type="button" onClick={() => pick(iso)}
              className={`rounded-full border px-2.5 py-1 text-xs ${value === iso
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                : 'border-emerald-100 bg-emerald-50/40 text-emerald-700 hover:border-emerald-300'}`}>
              {text}
            </button>
          );
        })}
        {recent && AGO.map(([mins, text]) => (
          <button key={mins} type="button"
            onClick={() => pick(new Date(Date.now() - mins * 60000).toISOString())}
            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 hover:border-indigo-300 hover:text-indigo-700">
            {text}
          </button>
        ))}
        {(suggestions || []).map((s) => (
          <button key={`${s.label}-${s.at}`} type="button" onClick={() => pick(s.at)}
            className={`rounded-full border px-2.5 py-1 text-xs ${value === s.at
              ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
              : 'border-indigo-100 bg-indigo-50/40 text-indigo-700 hover:border-indigo-300'}`}>
            {s.label} · {fmtTime(s.at)}
          </button>
        ))}
      </div>
      <input type="datetime-local" value={toLocalInput(value)}
        onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-700" />
    </div>
  );
}

export default function StepTimesDialog() {
  const [req, setReq] = useState(null);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const resolver = useRef(null);

  const finish = useCallback((saved) => {
    if (resolver.current) resolver.current(saved);
    resolver.current = null;
    setReq(null);
    setData(null);
  }, []);

  useEffect(() => registerStepTimesHandler((request) => new Promise((resolve) => {
    if (resolver.current) resolver.current(false);
    resolver.current = resolve;
    setReq(request);
    setData(null);
    setRows({});
    setReason('');
    setNote('');
  })), []);

  useEffect(() => {
    if (!req) return;
    const params = req.tankLotId ? { tank_lot_id: req.tankLotId } : { production_lot_id: req.productionLotId };
    axios.get(`${API}/lot-times`, { params })
      .then(({ data: d }) => {
        setData(d);
        const init = {};
        d.steps.forEach((s) => {
          init[s.key] = {
            start: s.firm_start ? s.start : null, finish: s.firm_end ? s.end : null,
            certainty: s.state === 'not_recorded' ? 'unknown' : (s.estimated && s.firm_start ? 'approximate' : 'exact'),
            touched: false,
          };
        });
        setRows(init);
      })
      .catch((e) => {
        toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not load the lot\'s times');
        finish(false);
      });
  }, [req, finish]);

  if (!req) return null;

  const asked = req.steps && req.steps.length ? Object.fromEntries(req.steps.map((s) => [s.step, s.needs])) : null;
  const shown = (data?.steps || []).filter((s) => (asked ? asked[s.key] : s.state !== 'skipped'));
  // Work happening now (a station's Submit, a Done tap) vs a lot being filled in later.
  const latest = Math.max(0, ...(data?.steps || []).flatMap((s) => [s.start, s.end]
    .concat((s.suggest?.start || []).map((x) => x.at), (s.suggest?.end || []).map((x) => x.at)))
    .filter(Boolean).map((x) => new Date(x).getTime()));
  const recent = (req.mode && req.mode !== 'edit') || Date.now() - latest < 12 * 3600000;
  const set = (key, patch) => setRows((r) => ({ ...r, [key]: { ...r[key], ...patch, touched: true } }));
  const needsReason = shown.some((s) => rows[s.key]?.touched && rows[s.key]?.certainty !== 'exact');

  const save = async () => {
    const entries = [];
    for (const s of shown) {
      const r = rows[s.key];
      if (!r) continue;
      const needs = asked ? asked[s.key] : null;
      if (r.certainty === 'unknown') {
        if (r.touched || needs) entries.push({ step: s.key, certainty: 'unknown' });
        continue;
      }
      if (needs) {
        if (needs.includes('start') && !r.start) return toast.error(`Pick when ${s.label.toLowerCase()} started — or choose "Not known".`);
        if (needs.includes('finish') && !r.finish) return toast.error(`Pick when ${s.label.toLowerCase()} finished — or choose "Not known".`);
      }
      if (!r.touched && !needs) continue;
      if (r.start && r.finish && new Date(r.finish) < new Date(r.start)) {
        return toast.error(`${s.label} finishes before it starts.`);
      }
      entries.push({ step: s.key, start: r.start || null, finish: r.finish || null, certainty: r.certainty });
    }
    if (!entries.length) return finish(false);
    if (entries.some((e) => e.certainty !== 'exact') && !reason) return toast.error('Pick a reason for the approximate / not known times.');
    setSaving(true);
    try {
      await axios.post(`${API}/lot-times/stamp`, {
        tank_lot_id: data.tank_lot_id, entries,
        reason: reason || null, note: note.trim() || null,
      });
      toast.success('Times saved');
      announceStepTimesChanged({ tankLotId: data.tank_lot_id });
      finish(true);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not save the times');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) finish(false); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />
            Step times · {data?.lot_code || req.lotCode || 'lot'}
          </DialogTitle>
          <DialogDescription>
            {req.message || 'When did each step start and finish? Tap a time, or pick one the records already hold.'}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
        ) : shown.length === 0 ? (
          <p className="py-6 text-sm text-slate-500">This lot has no steps to time yet.</p>
        ) : (
          <div className="space-y-3">
            {shown.map((s) => {
              const r = rows[s.key] || {};
              const needs = asked ? asked[s.key] : ['start', 'finish'];
              return (
                <div key={s.key} className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{s.label}</span>
                      {s.state === 'done' && s.duration && (
                        <span className="text-xs text-slate-500">took {s.estimated ? '~' : ''}{s.duration}</span>
                      )}
                    </div>
                    <div className="inline-flex overflow-hidden rounded-md border border-slate-200 text-xs">
                      {CERTAINTY.map(([k, t]) => (
                        <button key={k} type="button" onClick={() => set(s.key, { certainty: k })}
                          className={`px-2.5 py-1 ${r.certainty === k ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  {r.certainty === 'unknown' ? (
                    <p className="text-xs text-slate-500">Saved as not known, with the reason below. It is never averaged.</p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {needs.includes('start') && (
                        <TimeField label="Started" value={r.start} suggestions={s.suggest?.start} recent={recent}
                          onChange={(v) => set(s.key, { start: v })} />
                      )}
                      {needs.includes('finish') ? (
                        <TimeField label="Finished" value={r.finish} suggestions={s.suggest?.end} recent={recent}
                          after={r.start || (s.firm_start ? s.start : null)}
                          onChange={(v) => set(s.key, { finish: v })} />
                      ) : (
                        <p className="self-end text-xs text-slate-500">Finished when you submit.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {needsReason && (
              <div className="grid gap-2 rounded-lg bg-amber-50 p-3 sm:grid-cols-2">
                <label className="text-xs font-medium text-amber-800">
                  Why no exact time?
                  <select value={reason} onChange={(e) => setReason(e.target.value)}
                    className="mt-1 w-full rounded-md border border-amber-200 bg-white px-2 py-1.5 text-sm text-slate-700">
                    <option value="">Pick a reason…</option>
                    {(data.reasons || []).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                  </select>
                </label>
                <label className="text-xs font-medium text-amber-800">
                  Note {reason === 'other' ? '(required)' : '(optional)'}
                  <input value={note} onChange={(e) => setNote(e.target.value)}
                    className="mt-1 w-full rounded-md border border-amber-200 bg-white px-2 py-1.5 text-sm text-slate-700" />
                </label>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => finish(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !data}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {req.mode && req.mode !== 'edit' ? 'Save and continue' : 'Save times'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
